/**
 * Rive layer for the BandarQQ benchmark port.
 *
 * Three stacked canvases — mirrors the three RiveWidget instances built by
 * packages/amgbandarqq_v2/lib/presentation/rive/rive_controller_manager.dart:
 *
 *     main    → bandarqq.riv  artboard "main"     state machines [Main, MainBetResult]
 *     betting → bandarqq.riv  artboard "betting"  state machine  Main
 *     topItem → bandarqq.riv  artboard "topItem"  state machine  Effect
 *
 * Inputs are cached PER state-machine and looked up by name. Each input is
 * classified by `StateMachineInputType` (every input has both `value` and
 * `fire()` — `fire()` is a no-op on Number/Boolean — so duck-typing fails).
 */
import {
  Rive,
  Layout,
  Fit,
  Alignment,
  EventType,
  StateMachineInputType,
  type ViewModelInstance,
  type ViewModelInstanceNumber,
} from '@rive-app/webgl2';

import { debugLog, warn } from '../util/logger';
import type { BetItemId } from '../types/game';
import { BET_ID_TO_BOX_INDEX } from '../types/game';
import type { BetCollection } from '../game/betCollection';
import {
  BandarEffectInputs,
  BandarMainInputs,
  BandarResultInputs,
  RiveEventNames,
  TextInputs,
} from './riveInputs';

type AnyInput =
  | { type: 'number'; name: string; set(v: number): void; get(): number }
  | { type: 'boolean'; name: string; set(v: boolean): void; get(): boolean }
  | { type: 'trigger'; name: string; fire(): void };

export type TargetKey = 'main' | 'betting' | 'topItem';

const FILES: Record<TargetKey, string> = {
  main: 'bandarqq.riv',
  betting: 'bandarqq.riv',
  topItem: 'bandarqq.riv',
} as const;

const ARTBOARDS: Record<TargetKey, string> = {
  main: 'main',
  betting: 'betting',
  topItem: 'topItem',
} as const;

/**
 * State machines per artboard. Where there's more than one, `inputs` is keyed
 * by `${smName}:${inputName}` so we can disambiguate (the `main` artboard has
 * both `Main` (girl / startBet / endBet triggers) and `MainBetResult` (the
 * yellow-box winner highlights).
 */
const STATE_MACHINES: Record<TargetKey, string[]> = {
  main: ['Main', 'MainBetResult'],
  betting: ['Main'],
  // The topItem artboard carries the result visuals on `Effect` (BoolVS /
  // dominoes) AND the red-tab "start bet" / "no more bet" banners on `Main`
  // (triRedTab1/2/3). Both SMs must run on this top-most (z-5) canvas, else the
  // banners — whose triggers live ONLY on `Main` — never fire/render.
  topItem: ['Effect', 'Main'],
} as const;

interface RiveTarget {
  key: TargetKey;
  file: string;
  stateMachines: string[];
  instance: Rive;
  /** Key = `${smName}:${inputName}` (when >1 SM) OR just inputName. */
  inputs: Map<string, AnyInput>;
}

// ----------------------------------------------------------------------------

export interface RiveLayerOptions {
  canvases: Record<TargetKey, HTMLCanvasElement>;
  assetBase?: string;
}

export type RiveEventTopic =
  | 'ready'
  | 'placeBetClick'
  | 'evtClickChips'
  | 'evtClearClick'
  | 'evtButtonPlayClick'
  | 'UIButtonClick'
  | 'evtResultStop'
  | 'evtYouWinEnd'
  | 'evtYouLoseEnd';

type RiveEventCallback = (data: Record<string, unknown>) => void;

// ----------------------------------------------------------------------------

export class RiveLayer {
  private readonly canvases: Record<TargetKey, HTMLCanvasElement>;
  private readonly assetBase: string;
  private readonly targets: Map<TargetKey, RiveTarget> = new Map();
  private readonly listeners: Map<string, Set<RiveEventCallback>> = new Map();

  // Pending text writes to be applied once the corresponding artboard exists.
  private pendingMinText: string | null = null;
  private pendingMaxText: string | null = null;
  private pendingSingleMode: boolean | null = null;

  // Betting artboard ViewModel (Rive data binding). BandarQQ's betting artboard
  // delivers the tapped bet's SERVER id (291..299) through the ViewModel number
  // property `selectedBetId` — NOT as a `placeBetClick` event property — so the
  // event bridge must read it from here. Mirrors the Flutter
  // rive_controller_manager `_bettingViewModelInstance.number('selectedBetId')`.
  private bettingVmi: ViewModelInstance | null = null;
  private bettingBetIdProp: ViewModelInstanceNumber | null = null;

  constructor(opts: RiveLayerOptions) {
    this.canvases = opts.canvases;
    this.assetBase = opts.assetBase ?? '/assets/Rive/';
  }

  async init(): Promise<void> {
    this.fitAllCanvases();
    window.addEventListener('resize', () => this.fitAllCanvases());

    await Promise.all([
      this.loadOne('main'),
      this.loadOne('betting'),
      this.loadOne('topItem'),
    ]);

    this.dumpInputs();
    this.applyPendingState();
    this.emit('ready', {});
  }

  private fitAllCanvases(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    for (const canvas of Object.values(this.canvases)) {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
    }
    for (const t of this.targets.values()) {
      try {
        t.instance.resizeDrawingSurfaceToCanvas();
      } catch {
        /* ignore */
      }
    }
  }

  private loadOne(key: TargetKey): Promise<void> {
    return new Promise((resolve, reject) => {
      const file = FILES[key];
      const artboard = ARTBOARDS[key];
      const sms = STATE_MACHINES[key];
      const canvas = this.canvases[key];
      const src = `${this.assetBase}${file}`;
      const rive = new Rive({
        canvas,
        src,
        artboard,
        stateMachines: sms.length === 1 ? sms[0] : sms,
        autoplay: true,
        // The betting artboard exposes the tapped bet id via a data-bound
        // ViewModel (`BettingViewModel.selectedBetId`); autoBind makes the
        // runtime bind its default instance so the state machine writes — and
        // our reads — share the same instance.
        autoBind: key === 'betting',
        layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
        onLoad: () => {
          try {
            const inputs = this.cacheInputs(rive, sms);
            const target: RiveTarget = {
              key,
              file,
              stateMachines: sms,
              instance: rive,
              inputs,
            };
            this.targets.set(key, target);
            if (key === 'betting') this.resolveBettingViewModel(rive);
            this.attachEventBridge(rive, key);
            try {
              rive.resizeDrawingSurfaceToCanvas();
            } catch {
              /* ignore */
            }
            resolve();
          } catch (e) {
            reject(e);
          }
        },
        onLoadError: (err: unknown) => {
          reject(
            new Error(
              `Failed to load ${src} [${artboard}/${sms.join(',')}]: ${String(err)}`,
            ),
          );
        },
      });
    });
  }

  private cacheInputs(rive: Rive, sms: string[]): Map<string, AnyInput> {
    const out = new Map<string, AnyInput>();
    for (const sm of sms) {
      let list: unknown[] = [];
      try {
        list = rive.stateMachineInputs(sm) ?? [];
      } catch (e) {
        warn('[Rive] stateMachineInputs failed for', sm, e);
        continue;
      }
      for (const raw of list) {
        const inp = raw as {
          name: string;
          type: number;
          value: number | boolean;
          fire(): void;
        };
        const cached: AnyInput = (() => {
          switch (inp.type) {
            case StateMachineInputType.Number:
              return {
                type: 'number',
                name: inp.name,
                set: (v: number) => {
                  inp.value = v;
                },
                get: () => Number(inp.value ?? 0),
              };
            case StateMachineInputType.Boolean:
              return {
                type: 'boolean',
                name: inp.name,
                set: (v: boolean) => {
                  inp.value = v;
                },
                get: () => Boolean(inp.value),
              };
            case StateMachineInputType.Trigger:
              return { type: 'trigger', name: inp.name, fire: () => inp.fire() };
            default:
              return null as unknown as AnyInput;
          }
        })();
        if (!cached) continue;
        // Always store under `sm:name` so it's reachable, and also under
        // bare `name` so callers that don't know the SM still find it. If a
        // name appears in multiple SMs, the LAST one wins on the bare key;
        // callers needing a specific SM should use the qualified key.
        out.set(`${sm}:${inp.name}`, cached);
        out.set(inp.name, cached);
      }
    }
    return out;
  }

  private dumpInputs(): void {
    for (const t of this.targets.values()) {
      const rows: Array<{ target: string; sm: string; name: string; type: string }> = [];
      for (const [key, inp] of t.inputs) {
        if (!key.includes(':')) continue; // qualified entries only
        const sm = key.split(':', 1)[0];
        rows.push({ target: t.key, sm, name: inp.name, type: inp.type });
      }
      // eslint-disable-next-line no-console
      console.groupCollapsed(
        `[Rive] ${t.key} (${t.file} / ${t.stateMachines.join('+')}) — ${rows.length} input(s)`,
      );
      // eslint-disable-next-line no-console
      console.table(rows);
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }

  private attachEventBridge(rive: Rive, target: TargetKey): void {
    try {
      rive.on(EventType.RiveEvent, (event: unknown) => {
        const e = event as { data?: { name?: string; properties?: Record<string, unknown> } };
        const name = e?.data?.name;
        if (!name) return;
        const props: Record<string, unknown> = {
          ...(e?.data?.properties ?? {}),
          __target: target,
        };
        // BandarQQ's betting artboard reports the tapped bet via the ViewModel
        // property `selectedBetId`, not as an event property — so `placeBetClick`
        // arrives with no usable betID. Read it from the ViewModel (authoritative,
        // mirrors the Flutter handler) and inject it for the orchestrator.
        if (name === RiveEventNames.placeBetClick) {
          const betId = this.readSelectedBetId();
          if (betId != null) props.betID = betId;
        }
        if (this.listeners.has(name)) {
          this.emit(name, props);
        } else {
          debugLog('[Rive][event:passthrough]', target, name, props);
        }
      });
    } catch (e) {
      warn('[Rive] event bridge attach failed', e);
    }
  }

  /**
   * Resolve (and, if needed, bind) the betting artboard's ViewModel instance so
   * `selectedBetId` is readable. With `autoBind: true` the runtime has already
   * bound the default instance — prefer that exact instance so we read the same
   * one the state machine writes to. Fall back to binding it by name.
   */
  private resolveBettingViewModel(rive: Rive): void {
    try {
      let vmi: ViewModelInstance | null = rive.viewModelInstance;
      if (!vmi) {
        const vm =
          rive.viewModelByName('BettingViewModel') ?? rive.defaultViewModel();
        vmi =
          vm?.defaultInstance() ??
          vm?.instanceByName('DefaultBettingInstance') ??
          vm?.instanceByName('BettingInstance') ??
          null;
        if (vmi) rive.bindViewModelInstance(vmi);
      }
      this.bettingVmi = vmi;
      this.bettingBetIdProp = vmi?.number('selectedBetId') ?? null;
      debugLog(
        '[Rive] betting ViewModel bound:',
        vmi != null,
        '— selectedBetId prop:',
        this.bettingBetIdProp != null,
      );
      if (!this.bettingBetIdProp) {
        warn(
          '[Rive] betting ViewModel `selectedBetId` not found — bet taps will carry no betID',
        );
      }
    } catch (e) {
      warn('[Rive] resolveBettingViewModel failed', e);
    }
  }

  /**
   * Read the currently-selected bet's server id (291..299) from the betting
   * artboard's ViewModel. Returns null when nothing is selected (value <= 0) or
   * the ViewModel is unavailable. Mirrors the Flutter
   * `_bettingViewModelInstance.number('selectedBetId')` read.
   */
  private readSelectedBetId(): number | null {
    try {
      if (!this.bettingBetIdProp && this.bettingVmi) {
        this.bettingBetIdProp = this.bettingVmi.number('selectedBetId');
      }
      const v = this.bettingBetIdProp?.value;
      if (typeof v === 'number' && v > 0) return Math.round(v);
    } catch (e) {
      warn('[Rive] readSelectedBetId failed', e);
    }
    return null;
  }

  // -----------------------------------------------------------------
  // Public API — mirrors RiveControllerManager
  // -----------------------------------------------------------------

  /**
   * "Start bet" banner (triRedTab2). The red-tab triggers live on the topItem
   * artboard's `Main` SM (the top-most z-5 canvas, so it's visible). `main` is a
   * harmless fallback (its copy is occluded at z-1). NOT on betting — that
   * artboard has no red-tab.
   */
  fireStartBet(): void {
    this.fireTriggerAny('topItem', BandarMainInputs.trigStartBet, 'Main');
    this.fireTriggerAny('main', BandarMainInputs.trigStartBet, 'Main');
  }

  /** "No more bet" banner (triRedTab1) — topItem artboard's `Main` SM. */
  fireEndBet(): void {
    this.fireTriggerAny('topItem', BandarMainInputs.trigEndBet, 'Main');
    this.fireTriggerAny('main', BandarMainInputs.trigEndBet, 'Main');
  }

  /** Girl animator. 0=idle, 1=letsPlay, 2=takeChip. */
  triggerGirlAnim(state: 0 | 1 | 2): void {
    this.setNumberAny('main', BandarMainInputs.numGirl, state, 'Main');
  }

  setSingleMode(on: boolean): void {
    this.pendingSingleMode = on;
    this.setBoolAny('main', BandarMainInputs.boolIsSingleMode, on, 'Main');
    this.setBoolAny('betting', BandarMainInputs.boolIsSingleMode, on, 'Main');
    this.setBoolAny('topItem', BandarMainInputs.boolIsSingleMode, on, 'Effect');
  }

  setSoundOn(on: boolean): void {
    this.setBoolAny('main', BandarMainInputs.boolIsSoundOn, on, 'Main');
    this.setBoolAny('betting', BandarMainInputs.boolIsSoundOn, on, 'Main');
    this.setBoolAny('topItem', BandarEffectInputs.boolIsSoundOn, on, 'Effect');
  }

  // ----- Result wiring (topItem Effect SM) ---------------------------

  /** Set the 4 domino sprite indices [redA, redB, blueA, blueB]. */
  setDominoSpriteIndices(indices: number[]): void {
    if (indices.length < 4) return;
    this.setDominoFace(1, indices[0]);
    this.setDominoFace(2, indices[1]);
    this.setDominoFace(3, indices[2]);
    this.setDominoFace(4, indices[3]);
  }

  /**
   * Drive one domino tile's face value (which of the 28 domino sprites it
   * shows). Each tile is a reusable artboard instanced per slot, so the face is
   * a NESTED input — the top-level `Effect` SM input `dominoN` does NOT reach it
   * (that's why the result reveal showed stale/default faces). Mirrors the
   * Flutter `_setNumber('topItem','dominoN')` fallthrough to
   * `number('dominoN', path:'faceControl')` / `number('faceControl', path:'dominoN')`.
   *
   * The WebGL2 `setNumberStateAtPath` is a silent no-op (+console.warn) on a
   * form that doesn't exist and NEVER throws, so a return-value fallback chain
   * would short-circuit on the first (wrong) form. We therefore set every
   * candidate form unconditionally; only the real one takes effect.
   */
  private setDominoFace(slot: 1 | 2 | 3 | 4, value: number): void {
    const name = `domino${slot}`;
    // Top-level Effect input (harmless if the face is purely nested).
    this.setNumberAny('topItem', name, value, 'Effect');
    // Nested forms — one of these is the real per-tile face control.
    this.trySetNumberAtPathSilent('topItem', name, value, 'faceControl');
    this.trySetNumberAtPathSilent('topItem', 'faceControl', value, name);
  }

  showBandarResult(show: boolean): void {
    this.setBoolAny('topItem', BandarEffectInputs.boolVS, show, 'Effect');
  }

  /** Reset the domino "random shuffle" driver (Effect SM `resetDominoRandom`). */
  setResetDominoRandom(value: number): void {
    this.setNumberAny('topItem', BandarEffectInputs.numResetDominoRandom, value, 'Effect');
  }

  /**
   * Highlight winning bet boxes on the MainBetResult state machine
   * (`BoolyellowBox1..9`). Maps server bet IDs (291..299) → 1..9 via
   * BET_ID_TO_BOX_INDEX. Also drives `intQuiTwinTop` for the quick-twin
   * bonus boxes 294/297/298/295.
   */
  setWinningYellowBoxes(winningBetIds: ReadonlyArray<BetItemId>): void {
    for (let i = 1; i <= 9; i++) {
      const matched = winningBetIds.some(
        (id) => BET_ID_TO_BOX_INDEX[id] === i,
      );
      this.setBoolAny(
        'main',
        BandarResultInputs.boolYellowBox(i as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9),
        matched,
        'MainBetResult',
      );
    }

    let quiTwinTop = 0;
    for (const id of winningBetIds) {
      // bandarqq rive_controller_manager.dart:1266-1283
      if (id === 294) quiTwinTop = 2;
      else if (id === 297) quiTwinTop = 1;
      else if (id === 298) quiTwinTop = 3;
      else if (id === 295) quiTwinTop = 4;
    }
    this.setNumberAny('topItem', BandarEffectInputs.numQuiTwinTop, quiTwinTop, 'Effect');
  }

  /** Set the two pip-score numerals shown beside the dominoes. */
  setResultScores(playerScore: number, bankerScore: number): void {
    const t = this.targets.get('topItem');
    if (!t) return;
    const p = String(playerScore);
    const b = String(bankerScore);
    // The artboard exposes these text runs on the Effect SM.
    this.setTextRunOn(t, 'txtResult1', p);
    this.setTextRunOn(t, 'txtDominoResult1', p);
    this.setTextRunOn(t, 'txtResult2', b);
    this.setTextRunOn(t, 'txtDominoResult2', b);
  }

  /** Reset all result-time visuals (dominoes hidden, yellow boxes off, VS hidden). */
  resetBandarResult(): void {
    this.showBandarResult(false);
    this.fireTriggerAny('topItem', BandarEffectInputs.trigResetResult, 'Effect');
    this.setWinningYellowBoxes([]);
    this.setNumberAny('topItem', BandarEffectInputs.numQuiTwinTop, 0, 'Effect');
    this.setNumberAny('topItem', BandarEffectInputs.numResetDominoRandom, 0, 'Effect');
    const t = this.targets.get('topItem');
    if (t) {
      this.setTextRunOn(t, 'txtResult1', '');
      this.setTextRunOn(t, 'txtResult2', '');
      this.setTextRunOn(t, 'txtDominoResult1', '');
      this.setTextRunOn(t, 'txtDominoResult2', '');
    }
  }

  // ----- Text runs ---------------------------------------------------

  setBalanceText(value: string): void {
    this.setTextRunBroadcast(TextInputs.balance, value);
  }

  setBetAmountText(value: string): void {
    this.setTextRunBroadcast(TextInputs.betAmount, value);
  }

  setWinAmountText(value: string): void {
    this.setTextRunBroadcast(TextInputs.winAmount, value);
  }

  setBetLimits(minText: string, maxText: string): void {
    this.pendingMinText = minText;
    this.pendingMaxText = maxText;
    this.setTextRunBroadcast(TextInputs.minBet, minText);
    this.setTextRunBroadcast(TextInputs.maxBet, maxText);
  }

  // ----- Per-area bet text runs --------------------------------------

  setBetBoxAmount(boxIndex: number, amount: number): void {
    if (boxIndex < 1 || boxIndex > 9) return;
    const txt = amount <= 0 ? '' : formatBetBoxAmount(amount);
    const candidates = [
      `txtBetAmount${boxIndex}`,
      `BetAmount${boxIndex}`,
      `betAmount${boxIndex}`,
      `BettingAmount${boxIndex}`,
      `txtAmountBox${boxIndex}`,
    ];
    for (const name of candidates) this.setTextRunBroadcast(name, txt);
  }

  /** Push the full set of running totals. */
  setUserBetBoxAmounts(bets: BetCollection): void {
    for (let i = 1; i <= 9; i++) {
      const id = idForBox(i);
      const total = id != null ? bets.amountFor(id) : 0;
      this.setBetBoxAmount(i, total);
    }
  }

  clearAllBetBoxAmounts(): void {
    for (let i = 1; i <= 9; i++) this.setBetBoxAmount(i, 0);
  }

  // History bar: bandarqq.riv has no history input, so there is nothing to
  // drive here (the old setHistory/setAllHistory probed many name/path combos
  // and the WebGL2 runtime console.error'd on every miss). Removed.

  // -----------------------------------------------------------------
  // Pub/sub
  // -----------------------------------------------------------------

  on(topic: RiveEventTopic | string, cb: RiveEventCallback): () => void {
    let set = this.listeners.get(topic);
    if (!set) {
      set = new Set();
      this.listeners.set(topic, set);
    }
    set.add(cb);
    return () => set!.delete(cb);
  }

  private emit(topic: string, data: Record<string, unknown>): void {
    const set = this.listeners.get(topic);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(data);
      } catch (e) {
        warn('[Rive listener]', e);
      }
    }
  }

  // -----------------------------------------------------------------
  // Low-level helpers
  // -----------------------------------------------------------------

  private applyPendingState(): void {
    if (this.pendingSingleMode != null) this.setSingleMode(this.pendingSingleMode);
    if (this.pendingMinText != null && this.pendingMaxText != null) {
      this.setBetLimits(this.pendingMinText, this.pendingMaxText);
    }
  }

  private inputKey(name: string, sm?: string): string {
    return sm ? `${sm}:${name}` : name;
  }

  private setNumberAny(
    key: TargetKey,
    name: string,
    value: number,
    sm?: string,
  ): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(this.inputKey(name, sm)) ?? t.inputs.get(name);
    if (inp?.type === 'number') {
      inp.set(value);
      return true;
    }
    debugLog(`[Rive] number '${name}' not found on ${key}${sm ? `/${sm}` : ''}`);
    return false;
  }

  private setBoolAny(
    key: TargetKey,
    name: string,
    value: boolean,
    sm?: string,
  ): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(this.inputKey(name, sm)) ?? t.inputs.get(name);
    if (inp?.type === 'boolean') {
      inp.set(value);
      return true;
    }
    debugLog(`[Rive] bool '${name}' not found on ${key}${sm ? `/${sm}` : ''}`);
    return false;
  }

  private fireTriggerAny(
    key: TargetKey,
    name: string,
    sm?: string,
  ): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(this.inputKey(name, sm)) ?? t.inputs.get(name);
    if (inp?.type === 'trigger') {
      inp.fire();
      return true;
    }
    debugLog(`[Rive] trigger '${name}' not found on ${key}${sm ? `/${sm}` : ''}`);
    return false;
  }

  private trySetNumberAtPathSilent(
    key: TargetKey,
    inputName: string,
    value: number,
    path: string,
  ): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    try {
      t.instance.setNumberStateAtPath(inputName, value, path);
      return true;
    } catch {
      return false;
    }
  }

  /** Best-effort text-run write across every artboard that exposes it. */
  private setTextRunBroadcast(name: string, value: string): void {
    for (const t of this.targets.values()) this.setTextRunOn(t, name, value);
  }

  private setTextRunOn(t: RiveTarget, name: string, value: string): void {
    try {
      t.instance.setTextRunValue(name, value);
    } catch {
      /* not present on this artboard */
    }
  }
}

// -----------------------------------------------------------------

function formatBetBoxAmount(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(2)}K`;
  return value.toFixed(2);
}

function idForBox(boxIndex: number): BetItemId | null {
  for (const [id, idx] of Object.entries(BET_ID_TO_BOX_INDEX)) {
    if (idx === boxIndex) return Number(id) as BetItemId;
  }
  return null;
}
